/**
 * Utilitários de validação
 */

/**
 * Valida CNPJ
 */
export function isValidCNPJ(cnpj: string): boolean {
  const cleaned = cnpj.replace(/\D/g, '');

  // Deve ter 14 dígitos
  if (cleaned.length !== 14) return false;

  // Rejeita CNPJs conhecidos como inválidos
  if (/^(\d)\1+$/.test(cleaned)) return false;

  // Validação dos dígitos verificadores
  let sum = 0;
  let pos = 5;

  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned.charAt(i)) * pos;
    pos = pos === 2 ? 9 : pos - 1;
  }

  let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (digit !== parseInt(cleaned.charAt(12))) return false;

  sum = 0;
  pos = 6;

  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned.charAt(i)) * pos;
    pos = pos === 2 ? 9 : pos - 1;
  }

  digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return digit === parseInt(cleaned.charAt(13));
}

/**
 * Valida CPF
 */
export function isValidCPF(cpf: string): boolean {
  const cleaned = cpf.replace(/\D/g, '');

  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }

  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }

  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  return digit === parseInt(cleaned.charAt(10));
}

/**
 * Valida email
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Valida telefone brasileiro (10 ou 11 dígitos)
 */
export function isValidTelefone(telefone: string): boolean {
  const cleaned = telefone.replace(/\D/g, '');
  return cleaned.length === 10 || cleaned.length === 11;
}

/**
 * Valida se string não está vazia
 */
export function isNotEmpty(value: string): boolean {
  return value.trim().length > 0;
}

/**
 * Valida tamanho mínimo
 */
export function minLength(value: string, min: number): boolean {
  return value.length >= min;
}

/**
 * Valida tamanho máximo
 */
export function maxLength(value: string, max: number): boolean {
  return value.length <= max;
}

/**
 * Helper para validar formulário de investigação
 */
export interface InvestigationFormErrors {
  tipo?: string;
  valor?: string;
  cpf?: string;
  nome?: string;
  profundidadeMaxima?: string;
}

export function validateInvestigationForm(data: {
  tipo: string;
  valor: string;
  cpf?: string;
  nome?: string;
  profundidadeMaxima?: number;
}): InvestigationFormErrors {
  const errors: InvestigationFormErrors = {};

  // Validar tipo
  if (!['cnpj', 'email', 'telefone', 'cpf-nome'].includes(data.tipo)) {
    errors.tipo = 'Tipo inválido';
  }

  // Validar valor baseado no tipo
  if (!isNotEmpty(data.valor)) {
    errors.valor = 'Campo obrigatório';
  } else {
    switch (data.tipo) {
      case 'cnpj':
        if (!isValidCNPJ(data.valor)) {
          errors.valor = 'CNPJ inválido';
        }
        break;
      case 'email':
        if (!isValidEmail(data.valor)) {
          errors.valor = 'Email inválido';
        }
        break;
      case 'telefone':
        if (!isValidTelefone(data.valor)) {
          errors.valor = 'Telefone inválido (deve ter 10 ou 11 dígitos)';
        }
        break;
      case 'cpf-nome':
        // Valor livre para cpf-nome
        break;
    }
  }

  // Se tipo for cpf-nome, validar CPF e Nome
  if (data.tipo === 'cpf-nome') {
    if (!data.cpf || !isNotEmpty(data.cpf)) {
      errors.cpf = 'CPF obrigatório para este tipo';
    } else if (!isValidCPF(data.cpf)) {
      errors.cpf = 'CPF inválido';
    }

    if (!data.nome || !isNotEmpty(data.nome)) {
      errors.nome = 'Nome obrigatório para este tipo';
    } else if (!minLength(data.nome, 3)) {
      errors.nome = 'Nome deve ter no mínimo 3 caracteres';
    }
  }

  // Validar profundidade (se fornecida)
  if (data.profundidadeMaxima !== undefined) {
    if (data.profundidadeMaxima < 1 || data.profundidadeMaxima > 10) {
      errors.profundidadeMaxima = 'Profundidade deve estar entre 1 e 10';
    }
  }

  return errors;
}
